import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Lock, ShieldCheck } from 'lucide-react';
import { api } from '../api/client';
import AuthLayout from '../components/AuthLayout';
import ErrorBanner from '../components/ErrorBanner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Pick something at least 8 characters."
      footer={
        <Link to="/login" className="font-semibold text-brand-600 hover:underline">
          Back to sign in
        </Link>
      }
    >
      {!token ? (
        <ErrorBanner
          title="Missing reset token"
          body={
            <>
              Please request a new reset link from the{' '}
              <Link to="/forgot-password" className="font-semibold underline">
                forgot password
              </Link>{' '}
              page.
            </>
          }
        />
      ) : done ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 flex-none mt-0.5" />
          <div>Password updated. Redirecting to sign in…</div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">New password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                className="input pl-10"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="At least 8 characters"
              />
            </div>
          </div>
          <div>
            <label className="label">Confirm password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                className="input pl-10"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="Re-enter password"
              />
            </div>
          </div>
          {error && <ErrorBanner body={error} onDismiss={() => setError('')} />}
          <button className="btn-primary w-full" disabled={loading}>
            {loading ? (
              'Updating…'
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" /> Update password
              </>
            )}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
